import React, {
	Context,
	useContext,
	useMemo,
	useState,
} from 'react';
import { UPLOAD_TYPES } from '../types/constants';

type UploadDataSet = Record<UploadInterface['uploadIdentifier'], UploadInterface>

export interface UploadInterface {
	uploadName: string;
	uploadType: string;
	uploadIdentifier: string;
	uploadRequest: XMLHttpRequest;
}

export interface ProgressDataSet {
    [key: string]: number;
}

export interface AddUploadFunction {
	(name: string | undefined, type: string, identifier: string, request: XMLHttpRequest): void;
}

export interface UploadHookInterface {
	addUpload: AddUploadFunction;
	getUploadsByType: (type: string) => UploadInterface[];
	getProgressByIdentifier: (indentifier: string) => number;
	uploadData: UploadDataSet;
	mediaUploads: UploadInterface[];
	overlayUploads: UploadInterface[];
}

export const UploadContext = React.createContext({
	addUpload: () => undefined,
	getUploadsByType: () => [],
	getProgressByIdentifier: () => 0,
	uploadData: {},
	mediaUploads: [],
	overlayUploads: [],
}) as unknown as Context<UploadHookInterface>;

export function useUpload(): UploadHookInterface {
	const [uploadData, setUploadData] = useState<UploadDataSet>({});
	const [progressData, setProgressData] = useState<ProgressDataSet>({});

	const removeUploadById = (identifier: string): void => {
		setUploadData((prev: UploadDataSet) => {
			const newSet = { ...prev };
			delete newSet[identifier];
			return newSet;
		});
		setProgressData((prev: ProgressDataSet) => {
			const newSet = { ...prev };
			delete newSet[identifier];
			return newSet;
		});
	};

	const uploadProgress = (e: ProgressEvent, identifier: string) => {
		if (e.lengthComputable) {
			const prog = (e.loaded / e.total) * 100;
			setProgressData((prev: ProgressDataSet) => ({
				...prev,
				[identifier]: prog,
			}));
		}
	};

	const addUpload = (
		name: string | undefined,
		type: string,
		identifier: string,
		request: XMLHttpRequest,
	): void => {
		setUploadData((prev: UploadDataSet) => ({
			...prev,
			[identifier]: {
				uploadName: name ?? '',
				uploadType: type,
				uploadIdentifier: identifier,
				uploadRequest: request,
			},
		}));
		setProgressData((prev: ProgressDataSet) => ({
			...prev,
			[identifier]: 0,
		}));
		request.onreadystatechange = () => {
			if (request.readyState === XMLHttpRequest.DONE) {
				removeUploadById(identifier);
			}
		};
		request.upload.onprogress = (e) => uploadProgress(e, identifier);
		request.upload.onerror = () => removeUploadById(identifier);
		request.upload.onabort = () => removeUploadById(identifier);
	};

	const getUploadsByType = (type: string) => Object
		.values(uploadData)
		.filter((upload) => upload.uploadType === type);

	const getProgressByIdentifier = (identifier: string) => progressData[identifier];

	const mediaUploads = useMemo(() => getUploadsByType(UPLOAD_TYPES.MEDIA), [uploadData]);
	const overlayUploads = useMemo(() => getUploadsByType(UPLOAD_TYPES.OVERLAY), [uploadData]);

	return {
		addUpload,
		getUploadsByType,
		getProgressByIdentifier,
		uploadData,
		mediaUploads,
		overlayUploads,
	};
}

export const useGetPageUpload = (identifier: string) => {
	const {
		getUploadsByType,
		getProgressByIdentifier,
		uploadData,
	} = useContext(UploadContext);

	const pageUpload = useMemo(() => {
		const pageUploads = getUploadsByType(UPLOAD_TYPES.PAGE);
		return pageUploads.find((upload) => upload.uploadIdentifier === identifier) ?? null;
	}, [uploadData]);

	const progress = getProgressByIdentifier(identifier) ?? 0;

	return {
		pageUpload,
		progress,
	};
};

export const useGetProgressByIdentifier = (identifier: string) => {
	const { getProgressByIdentifier } = useContext(UploadContext);
	return getProgressByIdentifier(identifier);
};

export const UploadProvider = (
	props: any,
): React.ReactElement<any, React.Provider<UploadHookInterface>> => {
	const UploadContextValue = useUpload();
	return <UploadContext.Provider value={UploadContextValue}>
		{props.children}
	</UploadContext.Provider>;
};
